import { RotateCcw, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { navigationV2 } from "@src/shared/navigation/v2";

export interface ApplicationErrorPageV2Props {
  /**
   * Optional headline shown above the description. Defaults to a generic
   * "Something went wrong" copy when omitted.
   */
  title?: string;
  /**
   * Short, user-facing explanation of what happened. Defaults to a generic
   * apology when omitted. Sensitive details (stack traces, raw error
   * messages) should not be passed here.
   */
  description?: string;
  /**
   * Retry handler for the "Try again" action. Defaults to a full document
   * reload, which is what recovers a broken router or a failed chunk load.
   */
  onRetry?: () => void;
}

/**
 * Generic full-page fallback for the v2 route tree, rendered by
 * `ErrorBoundaryV2` for any error that isn't a 404. Mirrors the visual language
 * of `NotFoundPageV2` so the application never falls back to unstyled error
 * markup.
 */
export function ApplicationErrorPageV2({
  title = "Something went wrong",
  description = "An unexpected error occurred. Please try again, or head back home.",
  onRetry,
}: ApplicationErrorPageV2Props) {
  const handleRetry =
    onRetry ??
    (() => {
      window.location.reload();
    });

  return (
    <VerticalFlexbox
      alignItems="center"
      justifyContent="center"
      gap="md"
      className="min-h-[60vh] w-full px-6 py-12 text-center"
    >
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        className="size-[46px] rounded-[13px] bg-destructive/10 text-destructive"
      >
        <TriangleAlert className="size-[22px]" aria-hidden="true" />
      </VerticalFlexbox>
      <HeaderText
        tag="h1"
        className="max-w-[420px] text-balance text-[25px] leading-tight tracking-[-0.6px]"
      >
        {title}
      </HeaderText>
      <Text
        as="p"
        appearance="subdued"
        sizing="sm"
        className="max-w-[350px] text-balance leading-[1.55]"
      >
        {description}
      </Text>
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="sm"
        className="mt-2"
      >
        <Button
          type="button"
          variant="outline"
          className="h-[42px] rounded-[9px] text-[13.5px] font-semibold"
          onClick={handleRetry}
        >
          <RotateCcw className="size-4" aria-hidden="true" />
          Try again
        </Button>
        <Button
          asChild
          className="h-[42px] rounded-[9px] text-[13.5px] font-semibold"
        >
          <Link to={navigationV2.home()}>Back to Home</Link>
        </Button>
      </HorizontalFlexbox>
    </VerticalFlexbox>
  );
}
